import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { allNavItems, moreNavItems, navItems } from '../../data/navigation';
import { Ticker } from './Ticker';
import { Wordmark } from './Wordmark';
const EASE = [0.23, 1, 0.32, 1] as const;
export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const moreActive = moreNavItems.some(i => location.pathname.startsWith(i.to));
  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMoreOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);
  const linkCls = (isActive: boolean) => `relative block whitespace-nowrap px-2.5 py-2 text-[13.5px] transition-colors duration-150 ease-out ${isActive ? 'text-ink after:absolute after:inset-x-2.5 after:-bottom-[9px] after:h-[2px] after:bg-machine' : 'text-muted hover:text-ink'}`;
  return <header className="sticky top-0 z-40 bg-bg/95 backdrop-blur supports-[backdrop-filter]:bg-bg/85" data-id="element-410" data-mp="atd8j1swsb0t">
      <Ticker data-id="element-411" data-mp="6st7hn57sn9e" />
      <div className="border-b border-line" data-id="element-412" data-mp="6r8ei1yjtcdn">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between gap-6 px-5 md:px-8" data-id="element-413" data-mp="13bh3id83pvcs">
          <Link to="/" aria-label="AgentPopulation home" className="whitespace-nowrap" data-id="element-414" data-mp="yjfwp6tpcwu5">
            <Wordmark data-id="element-415" data-mp="2ss2w4ffzbtw6" />
          </Link>
          <nav aria-label="Primary" className="hidden lg:block" data-id="element-416" data-mp="2st4k1ki3gavb">
            <ul className="flex items-center gap-0.5" data-id="element-417" data-mp="2xrpd8zyxhua9">
              {navItems.map(item => <li key={item.to} data-id="element-418" data-mp="2j3b6idye5lof">
                  <NavLink to={item.to} end={item.end} className={({
                isActive
              }) => linkCls(isActive)} data-id="element-419" data-mp="2j29mhjzedouk">
                    {item.label}
                  </NavLink>
                </li>)}
              <li data-id="element-420" data-mp="2a0jv2e5impws">
                <div ref={moreRef} className="relative" data-id="element-421" data-mp="2a47mn7reh28v">
                  <button type="button" aria-expanded={moreOpen} aria-haspopup="true" onClick={() => setMoreOpen(o => !o)} className={`${linkCls(moreActive)} inline-flex items-center gap-1`} data-id="element-422" data-mp="2f1poke702h7d">
                    More
                    <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ${moreOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-423" data-mp="3ufvmy1apjcve" />
                  </button>
                  <AnimatePresence data-id="element-424" data-mp="1ntphenoeely8">
                    {moreOpen && <motion.ul initial={{
                    opacity: 0,
                    y: -4,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: -4,
                    scale: 0.98
                  }} transition={{
                    duration: 0.16,
                    ease: EASE
                  }} className="absolute right-0 top-full z-50 mt-3 w-52 origin-top-right rounded-md border border-line bg-surface p-1 shadow-2xl shadow-black/30" data-id="element-425" data-mp="1ivecmrcyn1mv">
                        {moreNavItems.map(item => <li key={item.to} data-id="element-426" data-mp="2gfqzswx7bjzw">
                            <NavLink to={item.to} className={({
                        isActive
                      }) => `block rounded px-3 py-2 text-[13.5px] ${isActive ? 'bg-raised text-ink' : 'text-muted hover:bg-raised hover:text-ink'}`} data-id="element-427" data-mp="2bk21sy0tyb5p">
                              {item.label}
                            </NavLink>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
              </li>
            </ul>
          </nav>
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line text-ink lg:hidden" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(o => !o)} data-id="element-428" data-mp="xjp3q7arhn1n">
            {open ? <XIcon className="h-4 w-4" data-id="element-429" data-mp="9s092kagwmhj" /> : <MenuIcon className="h-4 w-4" data-id="element-430" data-mp="ektaozan3epe" />}
          </button>
        </div>
        <AnimatePresence data-id="element-431" data-mp="3dal2ck4sn3qh">
          {open && <motion.nav id="mobile-nav" aria-label="Primary" initial={{
          opacity: 0,
          y: -6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} className="border-t border-line lg:hidden" data-id="element-432" data-mp="3d9s1fzsin8g9">
              <ul className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px px-5 py-3 sm:grid-cols-4" data-id="element-433" data-mp="1qdljpmw0kulc">
                {allNavItems.map(item => <li key={item.to} data-id="element-434" data-mp="1le8x93921pja">
                    <NavLink to={item.to} end={item.end} className={({
                isActive
              }) => `block rounded px-3 py-2.5 text-sm ${isActive ? 'bg-raised text-ink' : 'text-muted hover:text-ink'}`} data-id="element-435" data-mp="1gfnw91lzb1ts">
                      {item.label}
                    </NavLink>
                  </li>)}
              </ul>
            </motion.nav>}
        </AnimatePresence>
      </div>
    </header>;
}